import OptionButton from '@/components/v1/OptionButton'
import StepHeading from '@/components/v1/StepHeading'
import { type AnswerType } from '@app-studio-web/ui/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

// Long enough for the selected row to register visually before the funnel
// advances, short enough that the pacing still feels instant.
const AUTO_ADVANCE_DELAY_MS = 320

interface Props {
  answers: AnswerType[]
  /** Side effect the baseline page ran on submit, e.g. writing localStorage. */
  onSelect?: (value: string) => void
  subtitleKey?: string
  titleKey: string
}

export default function SingleSelectStep({
  answers,
  onSelect,
  subtitleKey,
  titleKey,
}: Props) {
  const [selected, setSelected] = useState<string>()
  const submit = useSubmit()
  const { t } = useTranslation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selected) return

    const { form, value } = event.currentTarget

    setSelected(value)
    onSelect?.(value)

    setTimeout(() => {
      if (form) submit(form)
    }, AUTO_ADVANCE_DELAY_MS)
  }

  return (
    <div className="flex grow flex-col gap-5 pt-1 pb-10">
      <StepHeading subtitleKey={subtitleKey} titleKey={titleKey} />

      <Form className="flex flex-col gap-2.5" method="post">
        {answers.map((answer, index) => {
          const value = answer.value
            ? t(answer.value)
            : t(answer.title, { lng: 'en' })

          return (
            <OptionButton
              animationDelayMs={120 + index * 50}
              emoji={answer.emoji}
              indicator="radio"
              isSelected={selected === value}
              key={answer.title}
              name={t(titleKey, { lng: 'en' })}
              onChange={handleChange}
              subtitle={answer.subtitle ? t(answer.subtitle) : undefined}
              title={t(answer.title)}
              value={value}
            />
          )
        })}
      </Form>
    </div>
  )
}
